import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Plus, Trash2, Download, LogOut, LayoutDashboard, Package, Users, Settings, X, Menu, Lock, FileText, Edit2, RotateCcw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from "sonner";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHART_TICK = { fontSize: 12 };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [leads, setLeads] = useState([]);
  
  // Product Form State
  const [newProduct, setNewProduct] = useState({ title: '', category_slug: '', image: '', dims: '', features: '', tag: '' });
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Category Form State
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryPdf, setNewCategoryPdf] = useState('');
  
  // Edit Category State
  const [editingCategory, setEditingCategory] = useState(null);

  // Password Change State
  const [creds, setCredentials] = useState({ new_email: '', new_password: '' });
  
  const navigate = useNavigate();
  const isLoggedIn = sessionStorage.getItem('admin_logged_in');

  const fetchData = useCallback(async () => {
      try {
          const [statsRes, prodRes, catRes] = await Promise.all([
              axios.get(`${API}/analytics/dashboard`),
              axios.get(`${API}/products`),
              axios.get(`${API}/categories`)
          ]);
          setStats(statsRes.data);
          setProducts(prodRes.data);
          setCategories(catRes.data);
          
          if (catRes.data.length > 0 && !newProduct.category_slug) {
              setNewProduct(prev => ({...prev, category_slug: catRes.data[0].slug}));
          }
      } catch (err) {
          if (err.response?.status === 401) navigate('/admin');
      }
  }, [navigate, newProduct.category_slug]);

  const fetchLeads = useCallback(async () => {
      try {
          const res = await axios.get(`${API}/leads/list`); 
          setLeads(res.data);
      } catch (err) {
          if (err.response?.status === 401) navigate('/admin');
      }
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
        navigate('/admin');
        return;
    }
    fetchData();
    fetchLeads();
  }, [isLoggedIn, navigate, fetchData, fetchLeads]);

  const handleLogout = async () => {
      try { await axios.post(`${API}/admin/logout`); } catch (_e) {}
      sessionStorage.removeItem('admin_logged_in');
      navigate('/admin');
  };

  const handleUpdateCredentials = async () => {
      if (!creds.new_password && !creds.new_email) return;
      try {
          await axios.post(`${API}/admin/change-password`, creds);
          toast.success("Credentials Updated! Please login again.");
          handleLogout();
      } catch (_err) {
          toast.error("Failed to update credentials");
      }
  };

  const handleFileUpload = (e, setter) => {
      const file = e.target.files[0];
      if (file) {
          if (file.size > 15 * 1024 * 1024) { // 15MB Limit
              toast.error("File is too large! Max 15MB.");
              return;
          }
          const reader = new FileReader();
          reader.onloadend = () => {
              setter(reader.result);
          };
          reader.readAsDataURL(file);
      }
  };

  const handleAddProduct = async () => {
      try {
          const payload = {
              ...newProduct,
              features: typeof newProduct.features === 'string' ? newProduct.features.split(',').map(f => f.trim()) : newProduct.features
          };
          await axios.post(`${API}/products`, payload);
          toast.success("Product Added");
          fetchData();
          setNewProduct({ title: '', category_slug: categories[0]?.slug || '', image: '', dims: '', features: '', tag: '' });
      } catch (err) {
          toast.error("Failed to add product");
      }
  };

  const handleUpdateProduct = async () => {
      if (!editingProduct) return;
      try {
          const payload = {
              ...editingProduct,
              features: typeof editingProduct.features === 'string' ? editingProduct.features.split(',').map(f => f.trim()) : editingProduct.features
          };
          await axios.put(`${API}/products/${editingProduct.id}`, payload);
          toast.success("Product Updated");
          setEditingProduct(null);
          fetchData();
      } catch (err) {
          toast.error("Failed to update product");
      }
  };

  const handleDeleteProduct = async (id) => {
      if(!window.confirm("Are you sure?")) return;
      try {
          await axios.delete(`${API}/products/${id}`);
          toast.success("Product Deleted");
          fetchData();
      } catch (err) {
          toast.error("Failed to delete");
      }
  };

  const handleResetCatalog = async () => {
      if(!window.confirm("WARNING: This will delete ALL current products and categories and reload the default catalog. Are you sure?")) return;
      try {
          await axios.post(`${API}/admin/reset-catalog`, {});
          toast.success("Catalog Reset Successfully");
          fetchData();
      } catch (err) {
          toast.error("Failed to reset catalog");
      }
  };

  const handleAddCategory = async () => {
      if (!newCategoryName) return;
      const slug = newCategoryName.toLowerCase().replace(/ /g, '-');
      try {
          await axios.post(`${API}/categories`, { name: newCategoryName, slug, pdf_url: newCategoryPdf });
          toast.success("Category Added");
          setNewCategoryName('');
          setNewCategoryPdf('');
          fetchData();
      } catch (err) {
          toast.error("Failed to add category");
      }
  };

  const handleUpdateCategory = async () => {
      if (!editingCategory) return;
      try {
          await axios.put(`${API}/categories/${editingCategory.id}`, editingCategory);
          toast.success("Category Updated");
          setEditingCategory(null);
          fetchData();
      } catch (err) {
          toast.error("Failed to update category");
      }
  };

  const handleDeleteCategory = async (id) => {
      if(!window.confirm("Delete category? This might break products linked to it.")) return;
      try {
          await axios.delete(`${API}/categories/${id}`);
          fetchData();
      } catch(err) {
          toast.error("Failed to delete category");
      }
  };

  const handleExportLeads = () => {
      window.open(`${API}/leads/export`, '_blank');
  };

  if (!stats) return <div className="p-8">Loading Dashboard...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 pb-24">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
            {/* Header - Stacked on Mobile, Row on Desktop */}
            <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-sm gap-4">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <img 
                        src="/assets/logo_RL_.png" 
                        alt="Royal Life" 
                        className="h-10 md:h-12 w-auto"
                    />
                    <div>
                        <h1 className="text-xl md:text-2xl font-serif font-bold text-royal-maroon">Admin Panel</h1>
                        <p className="text-[10px] md:text-xs text-gray-500">Steel Furniture Pvt Ltd</p>
                    </div>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    {/* Settings Dialog */}
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="flex-1 md:flex-none gap-2">
                                <Lock className="h-4 w-4" /> <span className="hidden md:inline">Credentials</span>
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Update Login Credentials</DialogTitle></DialogHeader>
                            <div className="space-y-4 py-4">
                                <p className="text-sm text-gray-500">Leave blank if you don't want to change.</p>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">New Email</label>
                                    <Input 
                                        placeholder="new@email.com" 
                                        value={creds.new_email}
                                        onChange={e => setCredentials({...creds, new_email: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">New Password</label>
                                    <Input 
                                        type="password"
                                        placeholder="New Password" 
                                        value={creds.new_password}
                                        onChange={e => setCredentials({...creds, new_password: e.target.value})}
                                    />
                                </div>
                                <Button onClick={handleUpdateCredentials} className="w-full bg-royal-maroon">Update & Logout</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                    
                    <Button variant="ghost" onClick={handleLogout} className="flex-1 md:flex-none gap-2 text-red-600 hover:bg-red-50">
                        <LogOut className="h-4 w-4" /> Logout
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="dashboard" className="space-y-6">
                <TabsList className="bg-white p-1 rounded-lg border shadow-sm w-full grid grid-cols-3 h-auto">
                    <TabsTrigger value="dashboard" className="gap-2 text-xs md:text-sm py-3"><LayoutDashboard className="h-4 w-4"/> <span className="hidden md:inline">Overview</span><span className="md:hidden">Stats</span></TabsTrigger>
                    <TabsTrigger value="products" className="gap-2 text-xs md:text-sm py-3"><Package className="h-4 w-4"/> Products</TabsTrigger>
                    <TabsTrigger value="leads" className="gap-2 text-xs md:text-sm py-3"><Users className="h-4 w-4"/> Leads</TabsTrigger>
                </TabsList>

                {/* Dashboard Tab */}
                <TabsContent value="dashboard" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-gray-500">Total Leads</CardTitle></CardHeader>
                            <CardContent><div className="text-3xl md:text-4xl font-bold text-royal-maroon">{stats.overview.total_leads}</div></CardContent>
                        </Card>
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-gray-500">Engagements</CardTitle></CardHeader>
                            <CardContent><div className="text-3xl md:text-4xl font-bold text-blue-600">{stats.overview.total_interactions}</div></CardContent>
                        </Card>
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-gray-500">Downloads</CardTitle></CardHeader>
                            <CardContent><div className="text-3xl md:text-4xl font-bold text-green-600">{stats.clicks.brochures}</div></CardContent>
                        </Card>
                    </div>

                    {/* Analytics Chart */}
                    <Card className="shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-lg">Engagement Trends</CardTitle>
                        </CardHeader>
                        <CardContent className="h-[250px] md:h-[300px] -ml-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={stats.chart_data}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={CHART_TICK} />
                                    <YAxis tick={CHART_TICK} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="clicks" stroke="#952F2F" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Products Tab */}
                <TabsContent value="products">
                    <Card className="shadow-sm border-0 md:border">
                        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-4 md:px-6">
                            <CardTitle>Product Catalog</CardTitle>
                            <div className="flex w-full md:w-auto gap-2 flex-wrap">
                                {/* Reset Catalog Button */}
                                <Button 
                                    variant="destructive" 
                                    className="gap-2 flex-1 md:flex-none"
                                    onClick={handleResetCatalog}
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset Catalog
                                </Button>

                                {/* Manage Categories Dialog */}
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" className="flex-1 md:flex-none gap-2"><Settings className="h-4 w-4"/> <span className="hidden md:inline">Categories</span></Button>
                                    </DialogTrigger>
                                    <DialogContent className="w-[90vw] max-w-lg rounded-xl">
                                        <DialogHeader><DialogTitle>Manage Categories</DialogTitle></DialogHeader>
                                        <div className="space-y-4 py-4">
                                            {/* Add New Category */}
                                            <div className="space-y-2 border-b pb-4">
                                                <h4 className="text-sm font-semibold">Add New Category</h4>
                                                <div className="flex flex-col gap-2">
                                                    <Input 
                                                        placeholder="Category Name (e.g. Hospital)" 
                                                        value={newCategoryName} 
                                                        onChange={e => setNewCategoryName(e.target.value)} 
                                                    />
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-medium">Category Brochure (PDF)</label>
                                                        <Input 
                                                            type="file" 
                                                            accept="application/pdf"
                                                            onChange={(e) => handleFileUpload(e, setNewCategoryPdf)} 
                                                        />
                                                        {newCategoryPdf && <span className="text-xs text-green-600">PDF Selected</span>}
                                                    </div>
                                                    <Button onClick={handleAddCategory}>Add Category</Button>
                                                </div>
                                            </div>

                                            {/* List Categories */}
                                            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                                                <h4 className="text-sm font-semibold">Existing Categories</h4>
                                                {categories.map(c => (
                                                    <div key={c.id} className="flex flex-col bg-gray-50 p-3 rounded gap-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium">{c.name}</span>
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => setEditingCategory(c)}>
                                                                    <Edit2 className="h-3 w-3" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCategory(c.id)}>
                                                                    <X className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        {c.pdf_url ? (
                                                            <a href={c.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
                                                                <FileText className="h-3 w-3" /> View Brochure
                                                            </a>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">No Brochure</span>
                                                        )}
                                                        
                                                        {/* Edit Mode */}
                                                        {editingCategory?.id === c.id && (
                                                            <div className="flex flex-col gap-2 mt-2 p-2 bg-white rounded border">
                                                                <label className="text-xs font-medium">Update Brochure</label>
                                                                <Input 
                                                                    type="file"
                                                                    accept="application/pdf"
                                                                    onChange={(e) => handleFileUpload(e, (val) => setEditingCategory({...editingCategory, pdf_url: val}))}
                                                                    className="h-9 text-xs"
                                                                />
                                                                <div className="flex gap-2 justify-end">
                                                                    <Button size="sm" variant="ghost" onClick={() => setEditingCategory(null)}>Cancel</Button>
                                                                    <Button size="sm" onClick={handleUpdateCategory}>Save</Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>

                                {/* Add Product Dialog */}
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button className="flex-1 md:flex-none bg-royal-maroon text-white gap-2"><Plus className="h-4 w-4"/> Add <span className="hidden md:inline">Product</span></Button>
                                    </DialogTrigger>
                                    <DialogContent className="w-[90vw] max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
                                        <DialogHeader><DialogTitle>Add New Product</DialogTitle></DialogHeader>
                                        <div className="space-y-4 py-4">
                                            <Input placeholder="Product Name" value={newProduct.title} onChange={e => setNewProduct({...newProduct, title: e.target.value})} />
                                            
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Product Image</label>
                                                <Input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (val) => setNewProduct({...newProduct, image: val}))} />
                                                {newProduct.image && <img src={newProduct.image} alt="Preview" className="h-20 object-contain rounded border" />}
                                            </div>

                                            <Input placeholder="Dimensions" value={newProduct.dims} onChange={e => setNewProduct({...newProduct, dims: e.target.value})} />
                                            <Input placeholder="Features (comma separated)" value={newProduct.features} onChange={e => setNewProduct({...newProduct, features: e.target.value})} />
                                            <Input placeholder="Tag (e.g. Best Seller)" value={newProduct.tag} onChange={e => setNewProduct({...newProduct, tag: e.target.value})} />
                                            
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Category</label>
                                                <select 
                                                    className="w-full p-2 border rounded-md bg-white"
                                                    value={newProduct.category_slug}
                                                    onChange={e => setNewProduct({...newProduct, category_slug: e.target.value})}
                                                >
                                                    {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                                                </select>
                                            </div>
                                            
                                            <Button onClick={handleAddProduct} className="w-full bg-royal-maroon">Save Product</Button>
                                        </div>
                                    </DialogContent>
                                </Dialog>

                                {/* Edit Product Dialog - Triggered via state */}
                                <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
                                    <DialogContent className="w-[90vw] max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
                                        <DialogHeader><DialogTitle>Edit Product</DialogTitle></DialogHeader>
                                        {editingProduct && (
                                            <div className="space-y-4 py-4">
                                                <Input 
                                                    placeholder="Product Name" 
                                                    value={editingProduct.title} 
                                                    onChange={e => setEditingProduct({...editingProduct, title: e.target.value})} 
                                                />
                                                
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Update Image</label>
                                                    <Input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (val) => setEditingProduct({...editingProduct, image: val}))} />
                                                    {editingProduct.image && <img src={editingProduct.image} alt="Preview" className="h-20 object-contain rounded border" />}
                                                </div>

                                                <Input 
                                                    placeholder="Dimensions" 
                                                    value={editingProduct.dims} 
                                                    onChange={e => setEditingProduct({...editingProduct, dims: e.target.value})} 
                                                />
                                                <Input 
                                                    placeholder="Features (comma separated)" 
                                                    value={Array.isArray(editingProduct.features) ? editingProduct.features.join(', ') : editingProduct.features} 
                                                    onChange={e => setEditingProduct({...editingProduct, features: e.target.value})} 
                                                />
                                                <Input 
                                                    placeholder="Tag (e.g. Best Seller)" 
                                                    value={editingProduct.tag || ''} 
                                                    onChange={e => setEditingProduct({...editingProduct, tag: e.target.value})} 
                                                />
                                                
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Category</label>
                                                    <select 
                                                        className="w-full p-2 border rounded-md bg-white"
                                                        value={editingProduct.category_slug}
                                                        onChange={e => setEditingProduct({...editingProduct, category_slug: e.target.value})}
                                                    >
                                                        {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                                                    </select>
                                                </div>
                                                
                                                <Button onClick={handleUpdateProduct} className="w-full bg-royal-maroon">Update Product</Button>
                                            </div>
                                        )}
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 md:p-6">
                            {/* Desktop Table View */}
                            <div className="hidden md:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Image</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Category</TableHead>
                                            <TableHead>Tag</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {products.map((p) => (
                                            <TableRow key={p.id}>
                                                <TableCell><img src={p.image} className="h-10 w-10 object-cover rounded border" alt="" /></TableCell>
                                                <TableCell className="font-medium">{p.title}</TableCell>
                                                <TableCell>{p.category_slug}</TableCell>
                                                <TableCell><span className="text-xs bg-gray-100 px-2 py-1 rounded">{p.tag || '-'}</span></TableCell>
                                                <TableCell>
                                                    <div className="flex gap-2">
                                                        <Button variant="ghost" size="icon" className="text-blue-600 hover:bg-blue-50" onClick={() => setEditingProduct(p)}>
                                                            <Edit2 className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteProduct(p.id)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile List View */}
                            <div className="md:hidden space-y-4 p-4 bg-gray-50">
                                {products.map((p) => (
                                    <div key={p.id} className="bg-white p-4 rounded-lg shadow-sm flex gap-4 items-center">
                                        <img src={p.image} className="h-16 w-16 object-cover rounded-md border shrink-0" alt="" />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-sm truncate">{p.title}</h4>
                                            <p className="text-xs text-gray-500">{p.category_slug}</p>
                                            {p.tag && <span className="text-[10px] bg-royal-maroon/10 text-royal-maroon px-2 py-0.5 rounded-full inline-block mt-1">{p.tag}</span>}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <Button variant="ghost" size="icon" className="text-blue-600 h-8 w-8 shrink-0" onClick={() => setEditingProduct(p)}>
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="text-red-500 h-8 w-8 shrink-0" onClick={() => handleDeleteProduct(p.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Leads Tab */}
                <TabsContent value="leads">
                    <Card className="shadow-sm border-0 md:border">
                        <CardHeader className="flex flex-row items-center justify-between px-4 md:px-6">
                            <CardTitle>Customer Leads</CardTitle>
                            <Button variant="outline" size="sm" className="gap-2" onClick={handleExportLeads}>
                                <Download className="h-4 w-4" /> <span className="hidden md:inline">Export CSV</span>
                            </Button>
                        </CardHeader>
                        <CardContent className="p-0 md:p-6">
                            {/* Desktop View */}
                            <div className="hidden md:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Phone</TableHead>
                                            <TableHead>Message</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {leads.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-8 text-gray-500">No leads found yet.</TableCell>
                                            </TableRow>
                                        ) : (
                                            leads.map((lead) => (
                                                <TableRow key={lead.id || Math.random()}>
                                                    <TableCell className="whitespace-nowrap">
                                                        {new Date(lead.created_at).toLocaleDateString()}
                                                        <span className="text-xs text-gray-400 block">{new Date(lead.created_at).toLocaleTimeString()}</span>
                                                    </TableCell>
                                                    <TableCell className="font-medium">{lead.name}</TableCell>
                                                    <TableCell>{lead.phone}</TableCell>
                                                    <TableCell className="max-w-md truncate" title={lead.message}>{lead.message}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile View */}
                            <div className="md:hidden space-y-3 p-4 bg-gray-50">
                                {leads.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">No leads found yet.</div>
                                ) : (
                                    leads.map((lead) => (
                                        <div key={lead.id || Math.random()} className="bg-white p-4 rounded-lg shadow-sm space-y-2">
                                            <div className="flex justify-between items-start">
                                                <h4 className="font-semibold">{lead.name}</h4>
                                                <span className="text-xs text-gray-400">{new Date(lead.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <div className="text-sm text-royal-maroon font-mono">{lead.phone}</div>
                                            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{lead.message}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    </div>
  );
}
